/**
 * A tiny `text/event-stream` frame parser (CR-BUILD-BOARD-EPICS-2026-09-26):
 * the transport's POST-SSE reader pushes decoded text chunks in, whole frames
 * come out. A frame is `event:` + one or more `data:` lines + a blank line;
 * chunks may split a frame, a line or a CRLF pair anywhere.
 *
 * `data` is parsed as JSON when it can be and kept as raw text otherwise; an
 * empty `data` is `{}`. Comment lines (`:`) and `id:` / `retry:` fields are
 * ignored — the PX server emits neither. Pure: no I/O, no store.
 */

export interface SseFrame {
  /** The `event:` name; `message` when the frame names none. */
  event: string
  data: unknown
}

export interface SseParser {
  /** Feed one decoded chunk; emits every frame it completes. */
  push(chunk: string): void
  /** End of stream: emits a trailing frame that had no closing blank line. */
  flush(): void
}

const FRAME_BREAK = /\r?\n\r?\n/

export function createSseParser(onFrame: (frame: SseFrame) => void): SseParser {
  let buffer = ''

  const emit = (raw: string): void => {
    let event = 'message'
    const data: string[] = []
    for (const line of raw.split(/\r?\n/)) {
      if (line.startsWith('event:')) event = line.slice(6).trim()
      else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''))
    }
    if (data.length === 0 && event === 'message') return
    const text = data.join('\n')
    let parsed: unknown = text
    if (text === '') {
      parsed = {}
    } else {
      try {
        parsed = JSON.parse(text) as unknown
      } catch {
        // Not JSON: the raw text is the payload (the slice ignores what it cannot read).
      }
    }
    onFrame({ event, data: parsed })
  }

  return {
    push(chunk: string): void {
      buffer += chunk
      for (let match = FRAME_BREAK.exec(buffer); match !== null; match = FRAME_BREAK.exec(buffer)) {
        emit(buffer.slice(0, match.index))
        buffer = buffer.slice(match.index + match[0].length)
      }
    },
    flush(): void {
      if (buffer.trim() !== '') emit(buffer)
      buffer = ''
    }
  }
}
